// app/[country]/[resort]/[chalet]/ChaletClientPage.tsx
"use client";

import React, { useState, useCallback, useRef } from "react";
import Header from "../../../components/contentparts/Header";
import type { SearchQuery } from "@/types/searchquery";
import { useParams, useRouter } from "next/navigation";

import ChaletBooking from "../../../components/DynamicPages/Chalet/ChaletBooking";
import ChaletImages from "../../../components/DynamicPages/Chalet/ChaletImages";
import QueryStringBuilder from "../../../components/QueryStringBuilder";
import Cookies from "universal-cookie";
import ReactGA from "react-ga4";
import { ChaletData } from "@/types/chaletData";

const ChaletClientPage = () => {
  const [chaletData, setChaletData] = useState<ChaletData | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState<SearchQuery | null>(null);

  const [bookingUrl, setBookingUrl] = useState(null);
  const chaletNameRef = useRef(null);
  const cookies = new Cookies();
  const apiUrl = process.env.NEXT_PUBLIC_REACT_APP_API_URL;
  const hasExecutedSearch = useRef(false);
  const router = useRouter();
  const params = useParams();
  // These will match your dynamic routes [country]/[resort]/[chalet]
  const country = params?.country?.toString() || "";
  const resort = params?.resort?.toString() || "";
  const executeSearch = useCallback(
    (searchQuery: SearchQuery) => {
      fetch(`${apiUrl}/chaletDetails`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(searchQuery),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Error: ${response.statusText}`);
          return response.json();
        })
        .then((data) => {
          setChaletData(data);
          setBookingUrl(data.bookingUrl);
          setDataLoaded(true);
        })
        .catch((error) => {
          console.error("Failed to fetch chalet details:", error);
        });
    },
    [apiUrl]
  );

  const handleChildData = useCallback(
    (searchQueryParams: SearchQuery | null) => {
      if (searchQueryParams?.queryStringNull) {
        const capitalise = (str: string) =>
          str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

        const capitalisedCountry = capitalise(country);
        const capitalisedResort = capitalise(resort);

        router.push(`/${capitalisedCountry}/${capitalisedResort}`);
        return;
      }

      if (!hasExecutedSearch.current && searchQueryParams) {
        hasExecutedSearch.current = true;
        executeSearch(searchQueryParams);
        setSearchQuery(searchQueryParams);
      }
    },
    [executeSearch, router, country, resort]
  );

  const formatCurrencyGBP = (amount: number) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const trackButtonClick = (ChaletName: string) => {
    const cookieConsent = cookies.get("cookieconsent");
    ReactGA.initialize("G-DN5MBZ5W53");
    if (cookieConsent === "true") {
      ReactGA.event("booking_click", {
        category: "Booking",
        label: "Chalet Booking Click for " + ChaletName,
      });
    }
  };

  return (
    <div>
      {/* Page Header */}
      <QueryStringBuilder sendDataToParent={handleChildData} />

      <Header
        PageName={
          chaletData?.name
            ? `Book the lovely ${chaletData.name} today`
            : "Book this Chalet Today"
        }
        PageBreadCrumbName="Find me a Chalet"
      />

      <section className="ftco-section-2">
        <div>
          <div className="row">
            {" "}
            {/* Setting a minimum height */}
            <div
              className="col-12 col-md-10 offset-md-1  p-5"
              style={{ backgroundColor: "#f2f2f2" }}
            >
              {/* Top Bar alone */}
              <div className="row">
                {" "}
                {/* Setting a minimum height */}
                <div className="col-md-8 col-12 col-xl-10">
                  <div className="row">
                    <div className="text-inner align-self-start">
                      <h2 ref={chaletNameRef}>{chaletData?.name}</h2>
                    </div>
                  </div>
                  <div className="row">
                    {/* Left Column for Hotel Details */}
                    <div className="col-10">
                      <div className="text-inner align-self-start">
                        <h6>
                          {dataLoaded ? chaletData?.resort.name : ""},{" "}
                          {dataLoaded ? chaletData?.resort.country.name : ""}{" "}
                        </h6>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right Column for Price and Booking */}
                <div className="col-md-4 col-12 col-xl-2 ftco-animate text-right mb-5">
                  <div className="text-inner align-self-start">
                    <h4>
                      Price:{" "}
                      {searchQuery
                        ? searchQuery.TotalCost !== null
                          ? formatCurrencyGBP(searchQuery.TotalCost)
                          : ""
                        : ""}
                    </h4>
                    {searchQuery && chaletData && chaletData.bookingUrl && (
                      <button
                        className="btn btn-primary"
                        onClick={() => {
                          trackButtonClick(chaletData.name);
                          if (chaletData.bookingUrl) {
                            window.open(chaletData.bookingUrl, "_blank");
                          } else {
                            console.warn(
                              "No booking URL available for this chalet."
                            );
                          }
                        }}
                      >
                        Book this Chalet
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-xl-8 col-lg-6 col-12">
                  <div className="DD">
                    {chaletData?.images && (
                      <ChaletImages images={chaletData.images} />
                    )}
                  </div>
                </div>
                <div className="col-xl-4 col-lg-6 col-12 mb-5 mt-lg-0 mt-5 f tco-animate text-right">
                  <div className="row">
                    {searchQuery && (
                      <ChaletBooking
                        chaletData={chaletData}
                        searchQuery={searchQuery}
                        bookingUrl={bookingUrl}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChaletClientPage;
